/**
 * Landmark Availability Gate — real-time stock check on Add-to-Cart
 *
 * @ui @landmark @availability @storefront
 *
 * For Landmark products ONLY, intercepts the product-form submit, asks the backend
 * (Shopify App Proxy → agora getAvailability) whether the requested quantity can be
 * fulfilled, and blocks the add ONLY on a definitive "not available" (shows a contact
 * message). Everything else — available, unknown, timeout, error, non-Landmark — lets
 * the add proceed normally; the cart "pending availability check" banner is the net.
 *
 * FAIL-SAFE BY DESIGN: a broken endpoint or JS error can never block Add-to-Cart.
 * The gate is a document-level capture-phase submit listener so it runs before the
 * theme's own handler; on "proceed" it re-submits with a one-shot pass flag so the
 * theme adds to cart normally.
 *
 * Requires: <product-form data-product-vendor="{{ product.vendor }}"> (buy-buttons.liquid)
 * and the standard <script data-variant> JSON (variant-picker.liquid) for the SKU.
 */
(function () {
  'use strict';

  var API = '/apps/trade-program/landmark-availability/check';
  var TIMEOUT_MS = 5000;
  // Matches "Landmark" AND "Landmark Ceramics" (the live storefront vendor name).
  function isLandmark(v) { return (v || '').indexOf('landmark') === 0; }
  // Debug: watch the browser console for [lmk-avail] lines while testing.
  function log() { try { console.log.apply(console, ['[lmk-avail]'].concat([].slice.call(arguments))); } catch (e) {} }

  function vendorOf(form) {
    try {
      var pf = form.closest('product-form');
      var v = (pf && pf.getAttribute('data-product-vendor')) ||
              form.getAttribute('data-product-vendor') || '';
      return v.trim().toLowerCase();
    } catch (e) { return ''; }
  }

  function skuOf(form) {
    try {
      // 1) SKU rendered server-side onto <product-form> (buy-buttons.liquid). Landmark
      //    products are single-variant, so this static value is authoritative.
      var pf = form.closest('product-form');
      var s = (pf && pf.getAttribute('data-variant-sku')) || form.getAttribute('data-variant-sku');
      if (s && s.trim()) return s.trim();
      var scope = form.closest('.shopify-section, section, product-info, quick-order-list, main') || document;
      // 2) Fallback: any element carrying the product sku (e.g. the sample button).
      var elSku = scope.querySelector('[data-product-sku]');
      if (elSku) { var ds = elSku.getAttribute('data-product-sku'); if (ds && ds.trim()) return ds.trim(); }
      // 3) Fallback: the standard variant JSON <script data-variant>.
      var el = scope.querySelector('script[data-variant]') || document.querySelector('script[data-variant]');
      if (el) { var v = JSON.parse(el.textContent || '{}'); if (v && v.sku) return String(v.sku); }
    } catch (e) {}
    return '';
  }

  function qtyOf(form) {
    try {
      var q = form.querySelector('[name="quantity"]');
      var n = q ? parseFloat(q.value) : 1;
      return (isFinite(n) && n > 0) ? n : 1;
    } catch (e) { return 1; }
  }

  // Flag (or clear) the line being added with a hidden line-item property so the cart
  // "pending availability check" banner shows for a Landmark item we could NOT verify
  // (fail-open: timeout / rate-limit / cold session / no SKU) while staying clear for a
  // verified one. Underscore-prefixed → hidden from the customer's cart line display
  // (line-item.liquid skips '_' keys). The native form submit carries it to /cart/add.
  function markPending(form, on) {
    try {
      var name = 'properties[_availability_pending]';
      var input = form.querySelector('input[name="' + name + '"]');
      if (on) {
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.appendChild(input);
        }
        input.value = '1';
      } else if (input && input.parentNode) {
        input.parentNode.removeChild(input);
      }
    } catch (e) {}
  }

  // Resolves to {available: true|false|null, ...}. NEVER rejects; times out to null.
  function check(sku, quantity) {
    return new Promise(function (resolve) {
      var settled = false;
      var to = setTimeout(function () {
        if (!settled) { settled = true; resolve({ available: null, source: 'timeout' }); }
      }, TIMEOUT_MS);
      var finish = function (d) {
        if (!settled) { settled = true; clearTimeout(to); resolve(d || { available: null }); }
      };
      try {
        fetch(API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sku: sku, quantity: quantity })
        }).then(function (r) {
          return r && r.ok ? r.json() : { available: null, source: 'http' };
        }).then(finish).catch(function () { finish({ available: null, source: 'error' }); });
      } catch (e) { finish({ available: null, source: 'exception' }); }
    });
  }

  function showBlock(form, result) {
    try {
      var msg = (result && result.message) ||
        "This quantity isn't currently available to ship. Please contact us to confirm availability.";
      var contact = (result && result.contact_url) || '/pages/contact';
      var host = form.querySelector('.product-form__buttons, .buy-buttons') || form;
      var box = form.querySelector('[data-lmk-avail-msg]');
      if (!box) {
        box = document.createElement('div');
        box.setAttribute('data-lmk-avail-msg', '');
        box.setAttribute('role', 'alert');
        box.style.cssText = 'margin-top:10px;padding:10px 12px;border:1px solid #d9b382;' +
          'background:#fff8ef;color:#7a4a12;border-radius:6px;font-size:0.9em;line-height:1.45;';
        host.appendChild(box);
      }
      box.textContent = msg + ' ';
      var a = document.createElement('a');
      a.href = contact; a.textContent = 'Contact us ›';
      a.style.cssText = 'text-decoration:underline;white-space:nowrap;';
      box.appendChild(a);
      // Re-enable any submit button the theme disabled during submit.
      form.querySelectorAll('[type=submit], button[name=add]').forEach(function (b) {
        b.removeAttribute('disabled'); b.classList.remove('is-loading', 'loading');
      });
    } catch (e) {}
  }

  function clearBlock(form) {
    try { var b = form.querySelector('[data-lmk-avail-msg]'); if (b) b.remove(); } catch (e) {}
  }

  // Spinner + "verifying…" shown right below the clicked button, during the check.
  function setVerifying(btn, on) {
    try {
      if (on) {
        if (!document.getElementById('lmk-spin-style')) {
          var st = document.createElement('style');
          st.id = 'lmk-spin-style';
          st.textContent = '@keyframes lmkspin{to{transform:rotate(360deg)}}';
          document.head.appendChild(st);
        }
        var box = document.createElement('div');
        box.setAttribute('data-lmk-verifying', '');
        box.setAttribute('role', 'status');
        box.style.cssText = 'display:flex;align-items:center;gap:8px;margin-top:10px;' +
          'font-size:0.9em;color:#555;line-height:1.4;';
        box.innerHTML =
          '<span style="width:16px;height:16px;flex:0 0 16px;border:2px solid #d9c3a0;' +
          'border-top-color:#7a4a12;border-radius:50%;display:inline-block;' +
          'animation:lmkspin .7s linear infinite;"></span>' +
          '<span>Verifying availability with the vendor…</span>';
        btn.insertAdjacentElement('afterend', box);
        btn.__lmkBox = box;
      } else if (btn.__lmkBox) {
        btn.__lmkBox.remove();
        btn.__lmkBox = null;
      }
    } catch (e) {}
  }

  function isAddButton(btn) {
    if (!btn) return false;
    var name = btn.getAttribute && btn.getAttribute('name');
    var type = btn.getAttribute && btn.getAttribute('type');
    return name === 'add' || type === 'submit' || btn.type === 'submit';
  }

  // Intercept the Add-to-Cart CLICK (robust across themes — works whether the theme
  // adds via a form submit or a click handler). Verify with the vendor first; then let
  // the click through natively (re-click with a one-shot pass flag) so the theme adds
  // exactly as it normally would. Fail-safe: any doubt → allow the add.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('button, input[type="submit"]');
    if (!isAddButton(btn)) return;
    if (btn.__lmkPass) { btn.__lmkPass = false; return; }     // re-click pass-through
    var form = btn.form || (btn.closest && btn.closest('form'));
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
    try {
      var vend = vendorOf(form);
      if (!isLandmark(vend)) return;                          // gated by vendor
      var sku = skuOf(form), qty = qtyOf(form);
      log('intercept — vendor:', vend, '| sku:', sku, '| qty:', qty);
      if (!sku) {
        log('no sku found → allowing (fail open, flagging pending)');
        markPending(form, true);   // couldn't identify the product → couldn't verify
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();                           // hold the theme's handler
      clearBlock(form);
      setVerifying(btn, true);
      check(sku, qty).then(function (result) {
        setVerifying(btn, false);
        log('result:', result);
        if (result && result.available === false) {
          showBlock(form, result);                            // definitive: BLOCK
          log('BLOCKED (out of stock)');
        } else {
          var verified = !!(result && result.available === true);
          markPending(form, !verified);      // fail-open (available !== true) → flag line
          btn.__lmkPass = true;              // available / unknown → add either way
          log(verified ? 'available → adding' : 'unverified (fail-open) → adding + flag pending');
          btn.click();
        }
      });
    } catch (err) {
      log('error (allowing add):', err);
    }
  }, true); // capture phase
})();
